import { useEffect, useContext, useRef } from 'react';
import _ from 'underscore';
import { GridContext } from '@bingads-webui-grid/grid-context';

async function defaultMakeCall(state, edm, odataParams) {
  const {
    pagination, editableFilter, sortedColumn, selectedColumns,
  } = state;
  const $select = _.compact(_.union(['Id'], selectedColumns.map(column => column.fieldName)));
  const $expand = _.compact(_.flatten(selectedColumns.map(column => column.expandProperties)));
  const sortKey = sortedColumn.sortKey || sortedColumn.fieldName;
  const query = {
    count: true,
    top: pagination.pageSize || 20,
    skip: ((pagination.pageNumber - 1) * pagination.pageSize) || 0,
    filter: { where: editableFilter },
    select: _.union($select, $expand),
    orderby: sortKey ? { [sortKey]: sortedColumn.isSortedDescending ? -1 : 1 } : undefined,
  };
  const parmas = {
    ...odataParams,
    query: {
      ...query,
      ...odataParams.query,
    },
  };
  const response = await edm.$makeCall(parmas).toPromise();
  return response;
}

export async function defaultMakeCollectionGetCall(state, edm, odataParams) {
  const {
    pagination, editableFilter, sortedColumn,
  } = state;
  const sortKey = sortedColumn.sortKey || sortedColumn.fieldName;
  const query = {
    count: true,
    top: pagination.pageSize || 20,
    skip: ((pagination.pageNumber - 1) * pagination.pageSize) || 0,
    filter: { where: editableFilter },
    orderby: sortKey ? { [sortKey]: sortedColumn.isSortedDescending ? -1 : 1 } : undefined,
    ...odataParams,
  };
  const response = await edm.get(query).toPromise();
  return response;
}

export const useCampaignGridDataSource = ({
  edm,
  makeCall = defaultMakeCall,
  odataParams = {},
  beforeCall = () => {},
  afterCall = () => {},
  onError = () => {},
  formatResponse = response => ({
    items: response.value,
    totalCount: response['@odata.count'],
    // We pass raw response to generate additional property like aggregatedItems in reducers, and keep logic outside reducers clean.
    response,
  }),
  shouldMakeCall = () => true,
}) => {
  const { state, dispatch } = useContext(GridContext);
  const apiIdForRereshGridData = useRef(0); // This is used for make sure render data of latest api call

  const {
    selectedColumns, sortedColumn, editableFilter, pagination, dataRefreshId,
  } = state;

  useEffect(() => {
    async function fetchData() {
      dispatch({ type: 'START_LOAD_DATA' });
      try {
        apiIdForRereshGridData.current += 1;
        const requestId = apiIdForRereshGridData.current;
        await beforeCall(state);
        const response = await makeCall(state, edm, odataParams);
        if (response && response.value) {
          if (requestId === apiIdForRereshGridData.current) {
            dispatch({ type: 'FINISH_LOAD_DATA', payload: formatResponse(response) });
          }
        } else {
          onError(response);
          dispatch({ type: 'FINISH_LOAD_DATA', payload: { items: [], totalCount: 0 } });
        }
      } catch (error) {
        dispatch({ type: 'FINISH_LOAD_DATA', payload: { items: [], totalCount: 0 } });
        onError(error);
      } finally {
        afterCall(state);
      }
    }

    if (selectedColumns && selectedColumns.length > 0 && shouldMakeCall(state)) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColumns, sortedColumn, editableFilter, pagination, dataRefreshId]);
};
